import { ErrorMessage } from '@hookform/error-message'
import { Button, Checkbox, Flex, Input, Tag, Typography, notification } from 'antd'
import { isEmpty } from 'lodash'
import React, { useEffect, useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { useLocalStorage } from 'usehooks-ts'

import { SYSTEM_MESSAGE_IDS } from 'src/app/shared/constants/systemMessages.constants'
import { IDefaultResponse, IOTPLogin } from 'src/app/shared/models/auth.model'
import { getPreAuthenInfo, resetCountOtpSent, updatePreAuthenInfo } from 'src/app/store/preAuthen'

import { LockOutlined, UserOutlined, WarningFilled } from '@ant-design/icons'
import ErrorText from 'src/app/shared/components/common/error-text'
import { FROM_RESET_LOGIN_PAGE_STATE } from 'src/app/shared/constants/common.constants'
import { lcStorageIsRememberMe, lcStorageUserName } from 'src/app/shared/constants/localStorage.constants'
import { SERVER_STATUS_CODES } from 'src/app/shared/constants/serverResponseStatusCodes.constants'
import { ResetPasswordType } from 'src/app/shared/models/common.model'
import { checkAccountValid, forgotPassword } from 'src/app/shared/services/AuthenticationService'
import { PatternValidate } from 'src/app/shared/utils'
import { getDeferredConfig } from 'src/app/store/masterSetup'
import '../shared/styles/index.scss'
import './index.scss'

const Login: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { state } = useLocation()

  const [loading, setLoading] = useState<boolean>(false)
  const [isDisableSendMailBtn, setIsDisableSendMailBtn] = useState<boolean>(false)
  const preAuthenInfo = useSelector(getPreAuthenInfo)
  const [isRememberMeStorage, setIsRememberMeStorage] = useLocalStorage(lcStorageIsRememberMe, false)
  const [userNameStorage, setUserNameStorage] = useLocalStorage(lcStorageUserName, '')
  const deferredConfig = useSelector(getDeferredConfig)
  const [isChanged, setIsChanged] = useState<boolean>(false)
  const initForm: IOTPLogin = {
    userName: '',
    password: '',
  }

  useEffect(() => {
    dispatch(resetCountOtpSent())
    return () => { }
  }, [preAuthenInfo])

  const {
    handleSubmit,
    control,
    formState: { isDirty, errors, isValid },
    setError,
    getValues,
    setValue,
  } = useForm<IOTPLogin>({ mode: 'all', defaultValues: initForm })

  const isDisabled = (!isValid && !isEmpty(errors)) || (!isChanged && !isDirty)

  const onSubmit: SubmitHandler<IOTPLogin> = (data) => {
    const payload = {
      userName: data.userName,
      password: data.password,
    }
    setLoading(true)

    checkAccountValid(payload)
      .then(
        (res: any) => {
          setUserNameStorage(data.userName ?? '')
          const { isFirstTimeLogin } = res?.data
          if (isFirstTimeLogin) {
            navigate('/reset-password', {
              state: {
                resetPasswordType: ResetPasswordType.FIRST_LOGIN,
                userName: getValues('userName'),
              },
            })
          } else {
            dispatch(updatePreAuthenInfo({ ...payload, email: res?.data?.email }))
            navigate('/login/otp', { state: { from: FROM_RESET_LOGIN_PAGE_STATE } })
          }
        },
        (err: IDefaultResponse<any>) => {
          handleErrorResponse(err)
        },
      )
      .finally(() => {
        setLoading(false)
      })
  }

  useEffect(() => {
    if (isRememberMeStorage) {
      setValue('userName', userNameStorage)

      if (!isEmpty(getValues('userName')) && !isEmpty(getValues('password'))) {
        setIsChanged(true)
      }
    }
  }, [])

  useEffect(() => {
    const hasStateErrorMessage = state?.errorMessage
    if (hasStateErrorMessage) {
      const errorTitle = state?.errorTitle
      const errorMessage = state?.errorMessage
      notification.error({
        message: <b>{errorTitle}</b>,
        description: errorMessage,
      })
      resetStateErrorMessage()
    }
  }, [])

  const resetStateErrorMessage = () => {
    if (state?.errorMessage) {
      navigate('', {
        state: {
          errorMessage: undefined,
        }
      })
    }
  }

  useEffect(() => {
    if (!isRememberMeStorage) {
      setUserNameStorage('')
    }
  }, [isRememberMeStorage])

  const handleErrorResponse = (err: IDefaultResponse<any>) => {
    switch (err?.statusCode) {
      case SYSTEM_MESSAGE_IDS.INCORRECT_USERNAME_PASSWORD:
      case SERVER_STATUS_CODES.PPI1_USER_NOT_FOUND:
        setError('userName', {
          type: 'required',
        })
        setError('password', {
          type: 'required',
          message: err?.message ?? '',
        })
        break
      case SERVER_STATUS_CODES.PPI6_FORCE_RESET_PASSWORD:
        navigate('/reset-password', {
          state: {
            resetPasswordType: ResetPasswordType.FORCE_RESET,
            userName: getValues('userName'),
          },
        })
        break

      case SERVER_STATUS_CODES.PPI5_RESEND_OTP_TOO_MANY_TIMES:
        navigate('/locked-user')
        break

      case SERVER_STATUS_CODES.PPI3_LOCK_USER:
        navigate('/locked-user')
        break

      case SERVER_STATUS_CODES.PPI4_USER_IS_INACTIVE:
        navigate('/inactive-user')
        break
      case SERVER_STATUS_CODES.PPI7_CLIENTUSER_CANNOT_LOGIN_HERE:
        window.location.href = 'https://sunwest.olbanking.com/onlineMessenger';
        break;
      default:
        break
    }
  }

  const handleOnChangeRememberMe = (e: any) => {
    setIsRememberMeStorage(e.target.checked)
  }

  const passwordValidateRule: any = {
    required: {
      value: true,
      message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.BLANK_PASSWORD),
    },
    minLength: {
      value: deferredConfig?.systemSettings?.passwordMinimumLength,
      message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.PASSWORD_IS_TOO_SHORT, {
        passwordMinimumLength: deferredConfig?.systemSettings?.passwordMinimumLength,
      }),
    },
    maxLength: {
      value: 128,
      message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.PASSWORD_LONGER_THAN_128_CHAR),
    },
    validate: {
      hasSpecialChar: (value: string) =>
        PatternValidate.specialCharacterPattern.test(value) ||
        t('SystemMessages:' + SYSTEM_MESSAGE_IDS.NEW_PASSWORD_NO_SPECIAL_CHAR),
      hasAtLeastOneNumberPattern: (value: string) =>
        PatternValidate.atLeastOneNumberPattern.test(value) ||
        t('SystemMessages:' + SYSTEM_MESSAGE_IDS.PASSWORD_NO_NUMBER),
      hasAtLeastOneUppercasePattern: (value: string) =>
        PatternValidate.atLeastOneUppercasePattern.test(value) ||
        t('SystemMessages:' + SYSTEM_MESSAGE_IDS.PASSWORD_NO_UPPERCASE),
      hasAtLeastOneLowercasePattern: (value: string) =>
        PatternValidate.atLeastOneLowercasePattern.test(value) ||
        t('SystemMessages:' + SYSTEM_MESSAGE_IDS.PASSWORD_NO_LOWERCASE),
    },
  }

  const handleOnChangeUserName = (e: any) => {
    setValue('userName', e.target.value, { shouldValidate: true })
  }

  const onClickForgotPassword = () => {
    const userName = getValues('userName')
    if (isEmpty(userName?.trim())) {
      notification.warning({
        message: <b>{t('LoginPage:forgot_password_empty_username_title')}</b>,
        description: t('LoginPage:forgot_password_empty_username'),
      })
      return
    }

    // call API send mail forgot password
    const forgotPasswordPayload = {
      userName,
    }
    setIsDisableSendMailBtn(true)
    forgotPassword(forgotPasswordPayload)
      .then(
        (res: any) => {
          notification.success({
            message: <b>{t('common:success')}</b>,
            description: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.SEND_EMAIL_FORGOT_PASSWORD_SUCCESS),
          })
        },
        (err: IDefaultResponse<any>) => {
          handleErrorResponse(err)
        },
      )
      .finally(() => {
        setIsDisableSendMailBtn(false)
      })
  }

  const handleAutoFill = () => {
    setIsChanged(true)
  }

  return (
    <div className='login'>
      <div className='login__container'>
        <div style={{ textAlign: 'center' }}>
          <Typography.Text
            style={{
              fontSize: '32px',
              fontWeight: 400,
              lineHeight: '33px',
              color: '#7B858B',
              margin: 0,
              textAlign: 'center',
            }}
            className='title'
          >
            The most effective safeguard <br></br> against transaction fraud.
          </Typography.Text>
          <Typography.Text
            style={{
              display: 'block',
              fontWeight: 700,
              fontSize: '28px',
              lineHeight: '33px',
              margin: '8px 0 8px 0',
            }}
            className='login-text'
          >
            {t('LoginPage:login_title')}
          </Typography.Text>
        </div>
        <Tag className='tag-item' icon={<WarningFilled style={{ fontSize: '18px' }} />} color='warning'>
          <Typography.Text style={{ fontSize: '14px' }}>{t('LoginPage:login_description_tag')}</Typography.Text>
        </Tag>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className='login-input' style={{ marginBottom: '20px' }}>
            <Controller
              render={({ field }) => (
                <React.Fragment>
                  <Typography.Text
                    strong
                    style={{ marginBottom: 4, textAlign: 'left', fontSize: '14px' }}
                    className='required'
                  >
                    {t('LoginPage:user_name_input.label')}
                  </Typography.Text>
                  <Input
                    {...field}
                    allowClear
                    size='large'
                    status={errors.userName ? 'error' : ''}
                    autoComplete='current-username'
                    prefix={<UserOutlined />}
                    placeholder={t('LoginPage:user_name_input.placeholder')}
                    onChange={handleOnChangeUserName}
                    onPressEnter={handleSubmit(onSubmit)}
                    maxLength={225}
                    onAnimationStart={handleAutoFill}
                  />
                </React.Fragment>
              )}
              rules={{
                required: {
                  value: true,
                  message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.BLANK_USERNAME),
                },
              }}
              name='userName'
              control={control}
            />
            <ErrorMessage errors={errors} name='userName' render={({ message }) => <ErrorText>{message}</ErrorText>} />
          </div>
          <div className='login-input' style={{ marginBottom: '20px' }}>
            <Controller
              render={({ field }) => (
                <React.Fragment>
                  <Typography.Text
                    strong
                    style={{ marginBottom: 4, textAlign: 'left', fontSize: '14px' }}
                    className='required'
                  >
                    {t('LoginPage:password_input.label')}
                  </Typography.Text>

                  <Input.Password
                    {...field}
                    allowClear
                    prefix={<LockOutlined />}
                    size='large'
                    status={errors.password ? 'error' : ''}
                    autoComplete='password'
                    placeholder={t('LoginPage:password_input.placeholder')}
                    onPressEnter={handleSubmit(onSubmit)}
                    maxLength={128}
                    onAnimationStart={handleAutoFill}
                  />
                </React.Fragment>
              )}
              rules={passwordValidateRule}
              control={control}
              name='password'
            />
            <ErrorMessage errors={errors} name='password' render={({ message }) => <ErrorText>{message}</ErrorText>} />
          </div>
          <div className='d-flex justify-content-space-between mb-40 remember-me'>
            <Flex gap={'small'} style={{ width: '100%', justifyContent: 'space-between' }}>
              <Checkbox checked={isRememberMeStorage} onChange={handleOnChangeRememberMe}>
                {t('LoginPage:remember_me_checkbox_text')}
              </Checkbox>
              <Typography.Link
                style={{ fontSize: '14px' }}
                disabled={isDisableSendMailBtn || loading}
                onClick={onClickForgotPassword}
              >
                {t('LoginPage:forgot_password_btn')}
              </Typography.Link>
            </Flex>
          </div>
          <Button
            size='large'
            onClick={handleSubmit(onSubmit)}
            type='primary'
            disabled={isDisabled}
            block
            loading={loading}
          >
            {t('LoginPage:login_btn')}
          </Button>
        </form>
      </div>
    </div>
  )
}

export default Login
